import React from "react";
import { Link } from "@tanstack/react-router";
import { Bell, BellRing, ChevronRight, CircleAlert } from "lucide-react";
import { useAuth } from "@core/auth/AuthProvider";
import { Flyover } from "@core/components/Flyover";
import { NOTIFICATION_EVENT, readDemoNotifications } from "@core/events/notification-bus";
import { useOrganization } from "@core/organizations/OrganizationProvider";
import { listNotifications } from "@modules/notifications/data/notifications-api";
import { relativeNotificationTime } from "@modules/notifications/domain/notification-domain";

function newestFirst(notifications) {
  return [...notifications].sort((left, right) => {
    const leftTime = Date.parse(left.createdAt ?? "") || 0;
    const rightTime = Date.parse(right.createdAt ?? "") || 0;
    return rightTime - leftTime;
  });
}

function NotificationPreviewRow({ notification, onNavigate }) {
  const content = (
    <>
      <span className="vp-notification-preview-icon" aria-hidden="true">
        {notification.priority === "high" ? <CircleAlert /> : <BellRing />}
      </span>
      <span className="vp-notification-preview-copy">
        <span>
          <strong>{notification.title}</strong>
          <time dateTime={notification.createdAt}>{relativeNotificationTime(notification.createdAt)}</time>
        </span>
        <small>{notification.message}</small>
      </span>
      {notification.route && <ChevronRight className="vp-notification-preview-chevron" aria-hidden="true" />}
    </>
  );

  const className = [
    "vp-notification-preview-row",
    notification.status === "unread" ? "is-unread" : "",
    notification.priority === "high" ? "is-high" : "",
  ].filter(Boolean).join(" ");

  if (!notification.route) return <div className={className}>{content}</div>;
  return <Link className={className} to={notification.route} onClick={onNavigate}>{content}</Link>;
}

export function NotificationSidebarAction({ item, active, open, onOpenChange }) {
  const { isDemoMode } = useAuth();
  const { activeOrganization } = useOrganization();
  const organizationId = activeOrganization?.id ?? null;
  const [filter, setFilter] = React.useState("recent");
  const [notifications, setNotifications] = React.useState([]);
  const [serverUnreadCount, setServerUnreadCount] = React.useState(0);
  const [loadedOrganizationId, setLoadedOrganizationId] = React.useState(null);
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState("");

  React.useEffect(() => {
    if (!open || !organizationId) return undefined;
    let cancelled = false;

    const load = async () => {
      setIsLoading(true);
      setError("");
      try {
        if (isDemoMode) {
          const next = newestFirst(readDemoNotifications(organizationId));
          if (!cancelled) {
            setNotifications(next);
            setServerUnreadCount(next.filter((notification) => notification.status === "unread").length);
            setLoadedOrganizationId(organizationId);
          }
        } else {
          const result = await listNotifications(organizationId);
          if (!cancelled) {
            setNotifications(newestFirst(result.notifications));
            setServerUnreadCount(result.unreadCount);
            setLoadedOrganizationId(organizationId);
          }
        }
      } catch (loadError) {
        if (!cancelled) setError(loadError instanceof Error ? loadError.message : "Notifications could not be loaded.");
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    void load();
    return () => { cancelled = true; };
  }, [isDemoMode, open, organizationId]);

  React.useEffect(() => {
    if (!open || !isDemoMode || !organizationId) return undefined;
    const handleChange = (event) => {
      if (event.detail?.organizationId !== organizationId) return;
      const next = newestFirst(readDemoNotifications(organizationId));
      setNotifications(next);
      setServerUnreadCount(next.filter((notification) => notification.status === "unread").length);
      setLoadedOrganizationId(organizationId);
    };
    window.addEventListener(NOTIFICATION_EVENT, handleChange);
    return () => window.removeEventListener(NOTIFICATION_EVENT, handleChange);
  }, [isDemoMode, open, organizationId]);

  React.useEffect(() => {
    if (!open) setFilter("recent");
  }, [open]);

  const scopedNotifications = loadedOrganizationId === organizationId ? notifications : [];
  const unreadNotifications = scopedNotifications.filter((notification) => notification.status === "unread");
  const unreadCount = Math.max(loadedOrganizationId === organizationId ? serverUnreadCount : 0, unreadNotifications.length);
  const visibleNotifications = (filter === "unread" ? unreadNotifications : scopedNotifications).slice(0, 10);

  const handlePrimaryClick = (event, toggle) => {
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    toggle();
  };

  return (
    <Flyover
      open={open}
      onOpenChange={onOpenChange}
      role="dialog"
      ariaLabel="Notification quick view"
      anchorClassName="vp-footer-flyover-anchor"
      panelClassName="vp-notification-popover"
      width={340}
      trigger={({ triggerProps, toggle, close }) => (
        <Link
          {...triggerProps}
          to={item.href}
          className={`vp-sidebar-control vp-footer-icon-control vp-notification-trigger ${active ? "is-active" : ""}`}
          aria-label={`${item.label}${unreadCount ? `, ${unreadCount} unread` : ""}`}
          title={`${item.label} (middle-click to open inbox)`}
          onClick={(event) => handlePrimaryClick(event, toggle)}
          onAuxClick={(event) => {
            if (event.button === 1) close("alternate-navigation");
          }}
        >
          <Bell aria-hidden="true" />
          {unreadCount > 0 && <span className="vp-notification-trigger-dot" aria-hidden="true" />}
        </Link>
      )}
    >
      {({ close }) => (
        <>
          <header className="vp-notification-popover-header">
            <span><strong>Notifications</strong><small>{unreadCount ? `${unreadCount} unread` : "You're all caught up"}</small></span>
            <Link to={item.href} onClick={() => close("navigate")}>View all</Link>
          </header>

          <div className="vp-notification-popover-tabs" role="tablist" aria-label="Quick view filter">
            <button type="button" role="tab" aria-selected={filter === "recent"} className={filter === "recent" ? "is-active" : ""} onClick={() => setFilter("recent")}>Recent</button>
            <button type="button" role="tab" aria-selected={filter === "unread"} className={filter === "unread" ? "is-active" : ""} onClick={() => setFilter("unread")}>Unread <span>{unreadCount}</span></button>
          </div>

          <div className="vp-notification-preview-list" aria-live="polite">
            {isLoading && scopedNotifications.length === 0 && <div className="vp-notification-preview-status">Loading notifications…</div>}
            {error && <div className="vp-notification-preview-status is-error" role="alert">{error}</div>}
            {!isLoading && !error && visibleNotifications.length === 0 && (
              <div className="vp-notification-preview-empty"><Bell aria-hidden="true" /><strong>{filter === "unread" ? "No unread notifications" : "No recent notifications"}</strong><span>New operational alerts will show up here.</span></div>
            )}
            {visibleNotifications.map((notification) => (
              <NotificationPreviewRow key={notification.id} notification={notification} onNavigate={() => close("navigate")} />
            ))}
          </div>

          <Link className="vp-notification-popover-footer" to={item.href} onClick={() => close("navigate")}>
            Open notification center <ChevronRight aria-hidden="true" />
          </Link>
        </>
      )}
    </Flyover>
  );
}
